import { useState } from "react";
import { useConsent } from "../../contexts/ConsentContext";

const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ${
      disabled
        ? "bg-brand-300 cursor-not-allowed"
        : checked
          ? "bg-brand-700 cursor-pointer"
          : "bg-neutral-300 cursor-pointer"
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
        checked ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const CATEGORIES = [
  {
    key: "necessary",
    label: "Necessary",
    description: "Essential for the website to function. Always enabled.",
    locked: true,
  },
  {
    key: "analytics",
    label: "Analytics",
    description:
      "Help us understand how visitors interact with our site so we can improve it.",
  },
  {
    key: "marketing",
    label: "Marketing",
    description:
      "Used to deliver relevant ads and measure campaign performance.",
  },
];

const CookieConsent = () => {
  const { bannerOpen, acceptAll, rejectAll, savePreferences } = useConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  if (!bannerOpen) return null;

  const handleSave = () => {
    savePreferences(prefs);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6">
      {/* Banner */}
      <div className="w-full max-w-2xl mx-auto bg-white rounded-ds-lg shadow-ds-lg border border-neutral-100 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <span className="material-symbols-outlined text-brand-700 text-2xl mt-0.5">
              cookie
            </span>
            <div>
              <h3 className="font-display text-lg font-bold text-neutral-900">
                Cookie Preferences
              </h3>
              <p className="text-sm text-neutral-500 mt-1 leading-relaxed">
                We use cookies to improve your experience and analyze site
                traffic. You can customize your preferences below.
              </p>
            </div>
          </div>

          {/* Details panel */}
          {showDetails && (
            <div className="mb-4 space-y-3 border-t border-neutral-100 pt-4">
              {CATEGORIES.map((cat) => (
                <div
                  key={cat.key}
                  className="flex items-start justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-neutral-800">
                      {cat.label}
                    </p>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {cat.description}
                    </p>
                  </div>
                  <Toggle
                    checked={cat.locked ? true : prefs[cat.key]}
                    disabled={cat.locked}
                    onChange={(val) =>
                      setPrefs((p) => ({ ...p, [cat.key]: val }))
                    }
                  />
                </div>
              ))}
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={acceptAll}
              className="flex-1 px-4 py-2.5 bg-brand-700 text-white text-sm font-semibold rounded-ds-md hover:bg-brand-800 transition-colors"
            >
              Accept All
            </button>
            <button
              onClick={rejectAll}
              className="flex-1 px-4 py-2.5 bg-neutral-100 text-neutral-700 text-sm font-semibold rounded-ds-md hover:bg-neutral-200 transition-colors"
            >
              Reject All
            </button>
            {showDetails ? (
              <button
                onClick={handleSave}
                className="flex-1 px-4 py-2.5 border border-brand-700 text-brand-700 text-sm font-semibold rounded-ds-md hover:bg-brand-50 transition-colors"
              >
                Save Preferences
              </button>
            ) : (
              <button
                onClick={() => setShowDetails(true)}
                className="flex-1 px-4 py-2.5 border border-neutral-200 text-neutral-600 text-sm font-semibold rounded-ds-md hover:bg-neutral-50 transition-colors"
              >
                Customize
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
